import Form from "views/Form";
import classNames from "classnames";
import { toast } from "react-hot-toast";
import { parseError } from "utils/helpers";
import { searchHandler } from "models/search";
import { dropdownItemSelector } from "models/form";
import { useDispatch, useSelector } from "react-redux";
import { setNavbarTitle } from "features/navbar/navbarSlice";
import { selectAllResults } from "features/search/searchSlice";
import FormElement from "components/Form/FormElement/FormElement";
import { createSearchParams, useNavigate } from "react-router-dom";
import { formConfig } from "pages/Customers/NewCustomer/formConfig";
import React, { useEffect, useLayoutEffect, useState } from "react";
import FormDropdown from "components/Form/FormDropdown/FormDropdown";
import { useNewCustomerMutation } from "features/customer/customerApiSlice";

import cardStyles from "styles/modules/Card.module.scss";
import formStyles from "components/Form/Form.module.scss";
import utilityStyles from "styles/modules/Utility.module.scss";

const form = new Form(formConfig);

const NewCustomer = () => {
  const [activeInput, setActiveInput] = useState("");
  const [formElements, setFormElements] = useState([]);
  const [formValidity, setFormValidity] = useState(false);
  const [showCallBillingDetails, setShowCallBillingDetails] = useState(false);
  const [showCallerIDDetails, setShowCallerIDDetails] = useState(false);
  const [showDidDetails, setShowDidDetails] = useState(false);

  const [extraFields, setExtraFields] = useState({
    call_billing_account_name: "Main",
    call_billing_account_number: "",
    call_billing_account_description: "Primary Account",
    callerid_value: "",
    did_number: "",
  });

  const searchResults = useSelector(selectAllResults);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [createCustomer, { isLoading }] = useNewCustomerMutation();

  useEffect(() => {
    dispatch(setNavbarTitle("New Customer"));
  }, [dispatch]);

  useLayoutEffect(() => {
    form.createFormElements();
    setFormElements(form.formElements);
  }, []);

  useEffect(() => {
    return () => {
      if (process.env.NODE_ENV === "production") {
        form.clear();
        form.setConfig(formConfig);
      }
    };
  }, []);

  const buildNestedFormData = () => {
    const data = {};
    for (const key in form.getFormConfig()) {
      const el = form.getFormConfig()[key];
      const value =
        key.split("_").pop() === "id"
          ? el.elementConfig["data-id"]
          : el.elementConfig.value;

      const keyParts = key.split(".");
      if (keyParts.length === 2) {
        if (!data[keyParts[0]]) data[keyParts[0]] = {};
        data[keyParts[0]][keyParts[1]] = value;
      } else {
        data[key] = value;
      }
    }

    if (showCallBillingDetails) {
      data.call_billing_account = {
        name: extraFields.call_billing_account_name,
        number: extraFields.call_billing_account_number,
        description: extraFields.call_billing_account_description,
      };
    }

    if (showCallerIDDetails) {
      data.account_caller_id = {
        callerid_number: extraFields.callerid_value,
      };
    }

    if (showDidDetails) {
      data.did = {
        number: extraFields.did_number,
      };
    }

    return data;
  };

  const resetForm = () => {
    form.clear();
    form.setConfig(formConfig);
    form.createFormElements();
    setFormElements(form.formElements);
    setFormValidity(false);
    setExtraFields({
      call_billing_account_name: "",
      call_billing_account_number: "",
      call_billing_account_description: "",
      callerid_value: "",
      did_number: "",
    });
    setShowCallBillingDetails(false);
    setShowCallerIDDetails(false);
    setShowDidDetails(false);
  };

  const submitFormHandler = async (e) => {
    e.preventDefault();
    if (!formValidity) return;

    try {
      const formData = buildNestedFormData();
      let response = await createCustomer(formData).unwrap();
      toast.success("Customer created successfully");
      setTimeout(() => {
        navigate({
          pathname: `/users/${response?.customer?.user?.id}`,
          search: createSearchParams({ page: "1" }).toString(),
        });
      }, 2000);
    } catch (error) {
      toast.error(parseError(error));
    }
  };

  const dropdownItemClickHandler = (key, item) => {
    dropdownItemSelector(key, item, form);
    setFormElements(form.formElements);
    setActiveInput("");
  };

  const inputChangeHandler = (e) => {
    const { id, value } = e.target;
    setActiveInput(id);

    if (extraFields.hasOwnProperty(id)) {
      setExtraFields((prev) => ({ ...prev, [id]: value }));
      return;
    }

    const updatedInput = form.inputChangeHandler(e);
    if (updatedInput) {
      if (["did_id", "caller_id", "call_billing_account_id"].includes(id)) {
        const endpointMap = {
          did_id: "/dids",
          caller_id: "/callerids",
          call_billing_account_id: "/call_accounts",
        };
        searchHandler(endpointMap[id], value, dispatch);
      }

      form.updateFormElement({ id, config: updatedInput });
      form.checkFormValidity();
      setFormElements(form.formElements);
      setFormValidity(form.isValid);
    }
  };

  return (
    <div className={`${utilityStyles.flexColumn} ${utilityStyles.itemsCenter}`}>
      <div className={`${cardStyles.menu} ${utilityStyles.wFull}`}>
        <h3>Fill in details for the new customer</h3>
      </div>

      <div className={formStyles.wrapper}>
        <form className={formStyles.container} onSubmit={submitFormHandler}>
          {formElements.map((el) => {
            const { title, elementType, elementConfig, ...config } = el.config;

            return (
              <div key={elementConfig.id} className={formStyles.group}>
                <FormElement
                  {...config}
                  title={title}
                  type={elementType}
                  id={elementConfig.id}
                  config={{ ...elementConfig, onChange: inputChangeHandler }}
                />

                <FormDropdown
                  items={searchResults}
                  itemClickHandler={(item) =>
                    dropdownItemClickHandler(elementConfig.id, item)
                  }
                  visible={
                    activeInput === elementConfig.id && el.config.dropdown
                  }
                />

                {elementConfig.id === "call_billing_account_id" && (
                  <>
                    <button
                      type="button"
                      className={formStyles.toggleButton}
                      onClick={() => setShowCallBillingDetails((prev) => !prev)}
                    >
                      {showCallBillingDetails
                        ? "Hide"
                        : "Create Call Billing Account"}
                    </button>

                    {showCallBillingDetails && (
                      <div className={formStyles.groupx}>
                        <FormElement
                          title="Name"
                          type="input"
                          id="call_billing_account_name"
                          config={{
                            id: "call_billing_account_name",
                            type: "text",
                            value: extraFields.call_billing_account_name,
                            placeholder: "Billing Name",
                            onChange: inputChangeHandler,
                          }}
                        />
                        <FormElement
                          title="Number"
                          type="input"
                          id="call_billing_account_number"
                          config={{
                            id: "call_billing_account_number",
                            type: "text",
                            value: extraFields.call_billing_account_number,
                            placeholder: "Billing Number",
                            onChange: inputChangeHandler,
                          }}
                        />
                        <FormElement
                          title="Description"
                          type="input"
                          id="call_billing_account_description"
                          config={{
                            id: "call_billing_account_description",
                            type: "text",
                            value: extraFields.call_billing_account_description,
                            placeholder: "Billing Description",
                            onChange: inputChangeHandler,
                          }}
                        />
                      </div>
                    )}
                  </>
                )}

                {elementConfig.id === "caller_id" && (
                  <>
                    <button
                      type="button"
                      className={formStyles.toggleButton}
                      onClick={() => setShowCallerIDDetails((prev) => !prev)}
                    >
                      {showCallerIDDetails ? "Hide" : "Create Caller ID"}
                    </button>

                    {showCallerIDDetails && (
                      <div className={formStyles.groupx}>
                        <FormElement
                          title="Caller ID (Name / Number)"
                          type="input"
                          id="callerid_value"
                          config={{
                            id: "callerid_value",
                            type: "text",
                            value: extraFields.callerid_value,
                            placeholder: "Caller ID",
                            onChange: inputChangeHandler,
                          }}
                        />
                      </div>
                    )}
                  </>
                )}

                {elementConfig.id === "did_id" && (
                  <>
                    <button
                      type="button"
                      className={formStyles.toggleButton}
                      onClick={() => setShowDidDetails((prev) => !prev)}
                    >
                      {showDidDetails ? "Hide" : "Create DID"}
                    </button>

                    {showDidDetails && (
                      <div className={formStyles.groupx}>
                        <FormElement
                          title="DID Number"
                          type="input"
                          id="did_number"
                          config={{
                            id: "did_number",
                            type: "text",
                            value: extraFields.did_number,
                            placeholder: "DID Number",
                            onChange: inputChangeHandler,
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          <div className={formStyles.btn}>
            <button disabled={isLoading || !formValidity}>
              {isLoading ? "Creating..." : "Create Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCustomer;
